import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { useIsMobile } from "@utils/hooks";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Title } from "@ui/components/containers/title";
import { ArrowIcon, ChevronDownIcon } from "@ui/components/icons";
import { Row, row, Stack } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { fromLeft, fromRight } from "@ui/styles/keyframes";
import { gap, mobile } from "@ui/styles/utils";
import { ApplicationTabs } from "./applicationTabs";
import { ReportTabs } from "./reportTabs";

const BACK_OFFICE_TABS = ["APPLICATIONS", "REPORTS"] as const;
const BACK_OFFICE_TABS_MOBILE = ["REPORTS"] as const;

export const BackofficePage: React.FC = () => {
	const isMobile = useIsMobile();

	const [tabIndex, setTabIndex] = useState<number | null>(isMobile ? null : 0);
	const TABS = !isMobile ? BACK_OFFICE_TABS : BACK_OFFICE_TABS_MOBILE;
	const currentTab = tabIndex !== null ? TABS[tabIndex] : undefined;

	return (
		<Container>
			{!isMobile || currentTab === undefined ? (
				<LeftMenuWrapper>
					{isMobile && <Title>{I18n.t("backoffice.page.title")}</Title>}
					<LeftMenu>
						<Title>{I18n.t("backoffice.page.title")}</Title>
						<Stack gap={20}>
							{TABS.map((tab, index) => (
								<TabView key={tab} onClick={() => setTabIndex(index)}>
									<Label selected={tabIndex === index}>
										<b>{I18n.t(`backoffice.tab.${tab}`)}</b>
									</Label>
									<ChevronDownIcon />
								</TabView>
							))}
						</Stack>
					</LeftMenu>
				</LeftMenuWrapper>
			) : null}
			{isMobile && currentTab ? (
				<MobileRightSide>
					<Row gap={20} onClick={() => setTabIndex(null)} align="center">
						<ArrowIcon style={{ cursor: "pointer" }} />
						<Title>
							<div>
								{currentTab === "APPLICATIONS"
									? I18n.t("backoffice.tab.APPLICATIONS")
									: I18n.t("backoffice.tab.REPORTS")}
							</div>
						</Title>
					</Row>
					{currentTab === "APPLICATIONS" ? <ApplicationTabs /> : <ReportTabs />}
				</MobileRightSide>
			) : currentTab === "REPORTS" ? (
				<ContentBlock>
					<ReportTabs />
				</ContentBlock>
			) : currentTab === "APPLICATIONS" ? (
				<ContentBlock>
					<ApplicationTabs />
				</ContentBlock>
			) : null}
		</Container>
	);
};

const Container = styled(Page)`
	display: grid;
	grid-template-columns: 280px 1fr;
	grid-template-rows: auto;
	grid-column-gap: 20px;
	grid-template-areas:
		"menu content"
		". content"
		". content"
		". content";

	@media ${mobile} {
		display: block;
	}
`;

const TabView = styled.div`
	cursor: pointer;
	width: 100%;
	${row(0, "space-between", "center")};
	padding: 5px 0px;
	will-change: transform;
	svg  {
		transition: transform ease-in-out 0.2s;
		transform: rotate(-90deg);
	}
	&:hover svg {
		transform: rotate(-90deg) translateY(2px);
	}
`;

const LeftMenuWrapper = styled.div`
	grid-area: menu;
	position: sticky;
	${gap(20)};
	@media ${mobile} {
		${gap(30)};
		animation: ${fromLeft} 0.2s linear;
	}
`;

const LeftMenu = styled(RoundedBlock)`
	grid-area: menu;
	position: sticky;
	padding: 40px 31px 40px 36px;
	${gap(38)};
`;

const ContentBlock = styled(RoundedBlock)`
	grid-area: content;
	padding: 60px 57px;
	${gap(60)};

	@media ${mobile} {
		padding: 22px;
	}
`;

const Label = styled(Primary)<{ selected?: boolean }>`
	color: ${({ theme, selected }) => (selected ? theme.colors.secondary : theme.colors.textPrimary)};
`;

const MobileRightSide = styled.div`
	${gap(30)};
	animation: ${fromRight} 0.2s linear;
`;
